(() => {
  if (customElements.get('nm-cart-drawer')) return;

  const CLOSE_ANIMATION_MS = 360;
  const REQUEST_TIMEOUT_MS = 8000;
  const REFRESH_ATTEMPTS = 2;
  const REFRESH_RETRY_MS = 240;
  const UNDO_TIMEOUT_MS = 7000;

  const wait = (milliseconds) => new Promise((resolve) => window.setTimeout(resolve, milliseconds));

  const nmT = (key, fallback, vars) => {
    let s = (window.NM_I18N && window.NM_I18N[key]) || fallback;
    if (vars) for (const k in vars) s = s.split('{' + k + '}').join(vars[k]);
    return s;
  };

  class NeverMondayCartDrawer extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.pageMode = this.dataset.pageMode === 'true';
      this.pendingQuantities = Object.create(null);
      this.confirmedQuantities = Object.create(null);
      this.busyCount = 0;
      this.activeRequests = new Set();
      this.pageLocked = false;
      this.inertedElements = [];
      this.contributionRoots = new Map();
      this.pendingContributionRoots = new WeakSet();
      customElements.whenDefined('contribe-plugin').then(() => {
        if (this.isConnected) this.normalizeContributionCards();
      });
      this.contributionObserver = new MutationObserver(() => this.normalizeContributionCards());
      this.contributionObserver.observe(this, { childList: true, subtree: true });
      this.normalizeContributionCards();

      this.handleClick = this.handleClick.bind(this);
      this.handleLocalSubmit = this.handleLocalSubmit.bind(this);
      this.handleDocumentClick = this.handleDocumentClick.bind(this);
      this.handleDocumentSubmit = this.handleDocumentSubmit.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleOpenEvent = this.handleOpenEvent.bind(this);
      this.handleAddedEvent = this.handleAddedEvent.bind(this);

      this.addEventListener('click', this.handleClick);

      if (this.pageMode) {
        this.addEventListener('submit', this.handleLocalSubmit);
        return;
      }

      document.addEventListener('click', this.handleDocumentClick);
      document.addEventListener('submit', this.handleDocumentSubmit);
      document.addEventListener('keydown', this.handleKeydown);
      document.addEventListener('nm:cart:open', this.handleOpenEvent);
      document.addEventListener('nm:cart:added', this.handleAddedEvent);
      document.addEventListener('eme:cart:open', this.handleOpenEvent);
      document.addEventListener('eme:cart:added', this.handleAddedEvent);

      const parameters = new URLSearchParams(window.location.search);
      if (parameters.get('cart') === 'open') {
        parameters.delete('cart');
        const query = parameters.toString();
        window.history.replaceState(
          {},
          '',
          window.location.pathname + (query ? `?${query}` : '') + window.location.hash
        );
        this.openAndRefresh(false);
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('submit', this.handleLocalSubmit);
      document.removeEventListener('click', this.handleDocumentClick);
      document.removeEventListener('submit', this.handleDocumentSubmit);
      document.removeEventListener('keydown', this.handleKeydown);
      document.removeEventListener('nm:cart:open', this.handleOpenEvent);
      document.removeEventListener('nm:cart:added', this.handleAddedEvent);
      document.removeEventListener('eme:cart:open', this.handleOpenEvent);
      document.removeEventListener('eme:cart:added', this.handleAddedEvent);

      window.clearTimeout(this.closeTimer);
      window.clearTimeout(this.errorTimer);
      window.clearTimeout(this.undoTimer);
      this.activeRequests?.forEach((controller) => controller.abort());
      this.activeRequests?.clear();
      this.contributionObserver?.disconnect();
      this.contributionRoots?.forEach((observer) => observer.disconnect());
      this.contributionRoots?.clear();
      this.finishContributionModal(false);
      if (!this.pageMode && this.pageLocked) this.unlockPage();
    }

    handleDocumentClick(event) {
      if (
        event.defaultPrevented ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey ||
        event.button !== 0
      ) return;

      const link = event.target.closest && event.target.closest('[data-cart-link]');
      if (!link) return;

      // The cart page already contains the current cart. Keep the anchor's normal /cart reload,
      // rather than opening the layout-level overlay with a second copy of the same section.
      if (document.querySelector('nm-cart-drawer[data-page-mode="true"]')) return;

      event.preventDefault();
      this.opener = link;
      this.openAndRefresh(false);
    }

    handleDocumentSubmit(event) {
      if (event.defaultPrevented) return;
      const form = event.target;
      if (!this.isAddForm(form)) return;
      if (form.closest('nm-cart-drawer[data-page-mode="true"]')) return;

      event.preventDefault();
      this.opener = event.submitter || document.activeElement;
      this.add(form, event.submitter);
    }

    handleLocalSubmit(event) {
      if (event.defaultPrevented || !this.isAddForm(event.target)) return;
      event.preventDefault();
      this.add(event.target, event.submitter);
    }

    isAddForm(form) {
      if (!form || !form.matches || form.hasAttribute('data-cart-no-ajax')) return false;
      const action = form.getAttribute('action') || '';
      return form.matches('form') && /\/cart\/add(?:\.js)?(?:[?#]|$)/.test(action);
    }

    handleOpenEvent(event) {
      const candidate = event?.detail?.opener || document.activeElement;
      if (candidate instanceof HTMLElement && document.contains(candidate)) this.opener = candidate;
      this.openAndRefresh(false);
    }

    handleAddedEvent(event) {
      // Existing PDP/quick-add code navigates to /cart unless a drawer claims this cancelable
      // event. Claiming the legacy alias keeps those integrations working during the rename.
      if (event.cancelable) event.preventDefault();
      const candidate = event.detail?.opener || event.detail?.submitter || document.activeElement;
      if (candidate instanceof HTMLElement && document.contains(candidate)) this.opener = candidate;
      this.setOpen(true);

      const suppliedSection = event.detail?.sections?.[this.sectionId];
      if (typeof suppliedSection !== 'string' || !suppliedSection.trim()) {
        this.openAndRefresh(true);
        return;
      }

      // An optional open-refresh may still be in flight when a third-party add completes.
      // Apply the confirmed post-add section only after older refreshes have settled, so stale
      // markup can never overwrite the newly added line.
      this.startBusy();
      this.enqueueMutation(async () => {
        try {
          const applied = await this.afterPendingRefreshes(() => this.applySectionHtml(suppliedSection));
          if (!applied) await this.refresh();
        } catch (error) {
          this.showError(error.userMessage);
        } finally {
          this.endBusy();
        }
      });
    }

    openAndRefresh(showRefreshError = false) {
      if (this.pageMode) return;
      this.setOpen(true);

      // The drawer is server-rendered with the current cart on every page load. A normal open
      // therefore needs no network request; only a confirmed external add without section HTML
      // uses the refresh fallback below.
      if (!showRefreshError) return;

      // A bundle integration may emit a canonical and a legacy alias in the same tick. Treat
      // them as one refresh while still preventing both events' fallback navigation.
      if (this.externalRefreshQueued) return;
      this.externalRefreshQueued = true;
      Promise.resolve().then(() => {
        this.externalRefreshQueued = false;
        this.refresh().catch(() => {
          // A plain open already has a complete server-rendered cart. If a preview proxy drops
          // the optional freshness request, keep that valid UI rather than alarming the shopper.
          // After a confirmed add, stale content matters and the failure remains visible.
          if (showRefreshError) this.showError();
        });
      });
    }

    handleKeydown(event) {
      if (this.dataset.state !== 'open') return;

      if (event.key === 'Escape') {
        event.preventDefault();
        if (this.contributionModal) {
          this.contributionModal.shadowRoot?.querySelector('.modal-close')?.click();
          return;
        }
        this.setOpen(false);
        return;
      }

      if (event.key !== 'Tab') return;
      const focusable = this.focusableElements();
      if (!focusable.length) {
        event.preventDefault();
        this.querySelector('[data-cart-panel]')?.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const activeElement = this.deepActiveElement();
      if (event.shiftKey && (activeElement === first || activeElement === this.querySelector('[data-cart-panel]'))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    focusableElements() {
      const panel = this.contributionModal?.getAttribute('open') === 'true'
        ? this.contributionModal.shadowRoot?.querySelector('.modal-container')
        : this.querySelector('[data-cart-panel]');
      if (!panel) return [];

      const selector = [
        'a[href]',
        'button:not(:disabled)',
        'input:not([type="hidden"]):not(:disabled)',
        'select:not(:disabled)',
        'textarea:not(:disabled)',
        'summary',
        '[tabindex]:not([tabindex="-1"])',
      ].join(', ');
      const elements = [];
      const walkComposedTree = (root) => {
        Array.from(root.children || []).forEach((element) => {
          if (element.matches(selector)) elements.push(element);
          if (element.shadowRoot) walkComposedTree(element.shadowRoot);
          else walkComposedTree(element);
        });
      };
      walkComposedTree(panel);

      return [...new Set(elements)].filter((element) => (
        element.tabIndex >= 0
        && !element.matches('[disabled], [aria-hidden="true"]')
        && !element.closest('[hidden], [inert], [aria-hidden="true"]')
        && (!element.closest('details:not([open])') || element.matches('summary'))
        && element.getClientRects().length > 0
        && getComputedStyle(element).display !== 'none'
        && getComputedStyle(element).visibility !== 'hidden'
      ));
    }

    deepActiveElement() {
      let activeElement = document.activeElement;
      while (activeElement?.shadowRoot?.activeElement) {
        activeElement = activeElement.shadowRoot.activeElement;
      }
      return activeElement;
    }

    handleClick(event) {
      const target = event.target;

      if (target.closest('[data-cart-error-close]')) {
        event.preventDefault();
        this.hideError();
        return;
      }

      if (target.closest('[data-cart-undo-action]')) {
        event.preventDefault();
        this.undoRemove();
        return;
      }

      if (target.closest('[data-cart-overlay]') || target.closest('[data-cart-close]')) {
        event.preventDefault();
        this.setOpen(false);
        return;
      }

      const row = target.closest('[data-cart-line]');
      if (!row) return;

      const key = row.dataset.lineKey;
      const printedQuantity = Number.parseInt(row.querySelector('[data-cart-qty-value]')?.textContent || '0', 10) || 0;
      const quantity = this.pendingQuantities[key] === undefined
        ? printedQuantity
        : this.pendingQuantities[key];

      if (target.closest('[data-cart-remove]')) {
        event.preventDefault();
        this.rememberFocus(row, 'remove');
        const removedItem = this.snapshotLine(row);
        this.queueQuantityChange(key, 0, row).then((succeeded) => {
          if (succeeded && removedItem) this.showUndo(removedItem);
        });
      } else if (target.closest('[data-cart-qty-decrease]')) {
        event.preventDefault();
        this.rememberFocus(row, 'decrease');
        const nextQuantity = Math.max(0, quantity - 1);
        const removedItem = nextQuantity === 0 ? this.snapshotLine(row) : null;
        this.queueQuantityChange(key, nextQuantity, row).then((succeeded) => {
          if (succeeded && removedItem) this.showUndo(removedItem);
        });
      } else if (target.closest('[data-cart-qty-increase]')) {
        event.preventDefault();
        const maximum = Number.parseInt(row.dataset.cartMax || '', 10);
        if (Number.isFinite(maximum) && quantity >= maximum) {
          this.showError(nmT('cart_stock_limit', 'Der er kun {count} tilbage på lager.', { count: maximum }));
          return;
        }
        this.rememberFocus(row, 'increase');
        this.queueQuantityChange(key, quantity + 1, row);
      }
    }

    rememberFocus(row, action) {
      const rows = Array.from(this.querySelectorAll('[data-cart-line]'));
      this.focusAfterRefresh = {
        key: row.dataset.lineKey,
        index: Math.max(0, rows.indexOf(row)),
        action,
      };
    }

    snapshotLine(row) {
      const id = Number.parseInt(row?.dataset.variantId || '', 10);
      const quantity = Number.parseInt(row?.dataset.lineQuantity || '', 10);
      if (!Number.isFinite(id) || !Number.isFinite(quantity) || quantity < 1) return null;

      let properties = {};
      try {
        const parsed = JSON.parse(row.dataset.cartProperties || '{}');
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) properties = parsed;
      } catch (error) {
        properties = {};
      }

      const sellingPlan = Number.parseInt(row.dataset.sellingPlanId || '', 10);
      return {
        id,
        quantity,
        properties,
        sellingPlan: Number.isFinite(sellingPlan) ? sellingPlan : null,
        title: row.querySelector('.nm-cart__item-title')?.textContent?.trim() || nmT('cart_item_fallback', 'Varen'),
      };
    }

    normalizeContributionCards() {
      if (this.normalizingContribution) return;
      this.normalizingContribution = true;

      try {
        const region = this.querySelector('[data-cart-contribution]');
        const slot = region?.querySelector('[data-cart-contribution-slot]');
        if (!region || !slot) {
          this.contributionRoots.forEach((observer) => observer.disconnect());
          this.contributionRoots.clear();
          return;
        }

        const cards = Array.from(this.querySelectorAll("contribe-plugin[id^='contribe_cart_']"));
        this.contributionRoots.forEach((observer, card) => {
          if (!cards.includes(card)) {
            observer.disconnect();
            this.contributionRoots.delete(card);
          }
        });
        cards.forEach((card) => {
          let shell = card.closest('[data-cart-contribution-shell]');
          if (!shell) {
            shell = document.createElement('div');
            shell.className = 'nm-cart__contribution-shell';
            shell.setAttribute('data-cart-contribution-shell', '');
            card.before(shell);
            shell.append(card);
          }
          if (shell.parentElement !== slot) slot.append(shell);
          this.alignContributionCard(card);
        });

        slot.querySelectorAll('[data-cart-contribution-shell]').forEach((shell) => {
          if (!shell.querySelector("contribe-plugin[id^='contribe_cart_']")) shell.remove();
        });
        region.hidden = cards.length === 0;
      } finally {
        this.normalizingContribution = false;
      }
    }

    alignContributionCard(card) {
      // Contribe renders in an open shadow root, so ordinary theme CSS cannot reach it.
      // Keep its project selection and information dialog intact; only align its UI.
      const root = card.shadowRoot;
      if (!root) {
        // Lit may attach the root after the host's insertion mutation. Retry once after
        // custom-element upgrade and paint; never poll an absent or incompatible app.
        if (!this.pendingContributionRoots.has(card)) {
          this.pendingContributionRoots.add(card);
          customElements.whenDefined('contribe-plugin').then(() => window.requestAnimationFrame(() => {
            if (this.isConnected && this.contains(card) && card.shadowRoot) this.alignContributionCard(card);
          }));
        }
        return;
      }
      if (!this.contributionRoots.has(card)) {
        const observer = new MutationObserver(() => this.alignContributionCard(card));
        observer.observe(root, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['class'] });
        this.contributionRoots.set(card, observer);
      }
      const selector = root.querySelector('#contribe-selector');
      if (!selector) return;
      selector.setAttribute('role', 'region');
      selector.setAttribute('aria-label', nmT('donation_select', 'Vælg donationsprojekt'));
      card.closest('[data-cart-contribution]')?.setAttribute('data-app-ready', 'true');
      if (!root.querySelector('[data-nm-cart-app-style]')) {
        const style = document.createElement('style');
        style.setAttribute('data-nm-cart-app-style', '');
        style.textContent = `
          #contribe-selector { font-family: var(--eme-font-body, Arial, sans-serif) !important; color: var(--nm-cart-ink, #0a0a0a); background: transparent !important; padding: 0 !important; }
          #contribe-selector-header { display: grid !important; grid-template-columns: minmax(0, 1fr) 44px; align-items: center; gap: 8px; margin: 0 0 8px !important; text-align: left !important; }
          #contribe-selector-header-title { order: 2; margin: 0 !important; }
          #contribe-selector-header-title > b { display: none; }
          #contribe-selector-header-title .contribe-read-more { display: grid !important; width: 44px !important; height: 44px !important; place-items: center; cursor: pointer; }
          #contribe-selector-header-description { order: 1; font-size: 12px !important; line-height: 18px !important; color: #606060; margin: 0 !important; text-align: left !important; }
          #contribe-selector-project-selector { gap: var(--nm-grid-gap, 4px) !important; }
          #contribe-selector-project-description { border: 0 !important; border-radius: 0 !important; box-shadow: none !important; background: transparent !important; margin-top: 8px !important; padding: 8px 0 0 !important; }
          #contribe-selector-project-description-title, #contribe-selector-project-description-description { font-size: 12px !important; line-height: 18px !important; }
          .contribe-selector-project-button { min-height: 48px; border-radius: var(--nm-cart-control-radius, 4px) !important; font-family: inherit; }
          button:focus-visible, [role="button"]:focus-visible { outline: 2px solid currentColor; outline-offset: -3px; }
        `;
        root.append(style);
      }
      let projects = [];
      try {
        projects = JSON.parse(document.querySelector('contribe-modal')?.getAttribute('pluginconfig') || '{}').projects || [];
      } catch (_) { /* The app may not have provided its configuration yet. */ }
      root.querySelectorAll('.contribe-selector-project-button').forEach((button, index) => {
        const selected = button.classList.contains('contribe-active');
        button.setAttribute('aria-pressed', String(selected));
        const projectName = projects[index]?.name || (selected && root.querySelector('#contribe-selector-project-description-title b')?.textContent.trim());
        if (projectName) button.setAttribute('aria-label', nmT('donation_support', 'Støt {name}', { name: projectName }));
        if (!button.getAttribute('aria-label') && !button.textContent.trim()) {
          button.setAttribute('aria-label', nmT('donation_select_n', 'Vælg donationsprojekt {number}', { number: index + 1 }));
        }
      });
      const info = root.querySelector('#contribe-selector-header-title .contribe-read-more');
      if (info && !info.hasAttribute('data-nm-keyboard')) {
        info.setAttribute('data-nm-keyboard', '');
        info.setAttribute('role', 'button');
        info.setAttribute('tabindex', '0');
        info.setAttribute('aria-label', nmT('donation_read_more', 'Læs om donationen'));
        info.addEventListener('click', () => {
          window.requestAnimationFrame(() => this.prepareContributionModal(info));
        });
        info.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          info.click();
        });
      }
    }

    prepareContributionModal(opener) {
      const modal = document.querySelector('contribe-modal');
      const root = modal?.shadowRoot;
      const container = root?.querySelector('.modal-container');
      const close = root?.querySelector('.modal-close');
      if (this.dataset.state !== 'open' || modal?.getAttribute('open') !== 'true' || !container || !close) return;
      this.finishContributionModal(false);
      this.contributionModal = modal;
      this.contributionModalOpener = opener;
      this.contributionModalWasInert = modal.inert;
      // This app portal lives outside the drawer, among the page elements we normally lock.
      modal.inert = false;
      this.querySelector('[data-cart-panel]').inert = true;
      container.setAttribute('role', 'dialog');
      container.setAttribute('aria-modal', 'true');
      container.setAttribute('aria-label', nmT('donation_about', 'Om donationen'));
      close.setAttribute('role', 'button');
      close.setAttribute('tabindex', '0');
      close.setAttribute('aria-label', nmT('donation_close', 'Luk donationsvinduet'));
      if (!root.querySelector('[data-nm-cart-modal-style]')) {
        const style = document.createElement('style');
        style.setAttribute('data-nm-cart-modal-style', '');
        style.textContent = `
          .modal-container { max-height: calc(100dvh - 32px) !important; overflow-y: auto; overscroll-behavior: contain; font-family: var(--eme-font-body, Arial, sans-serif) !important; border-radius: 4px; }
          .modal-close { display: grid !important; place-items: center; width: 44px !important; height: 44px !important; top: 0 !important; right: 0 !important; cursor: pointer; }
          .modal-close:focus-visible, a:focus-visible { outline: 2px solid #0a0a0a; outline-offset: -3px; }
        `;
        root.append(style);
      }
      if (!close.hasAttribute('data-nm-keyboard')) {
        close.setAttribute('data-nm-keyboard', '');
        close.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          close.click();
        });
      }
      this.contributionModalObserver = new MutationObserver(() => {
        if (modal.getAttribute('open') !== 'true') this.finishContributionModal();
      });
      this.contributionModalObserver.observe(modal, { attributes: true, attributeFilter: ['open'] });
      window.requestAnimationFrame(() => {
        if (this.contributionModal === modal) close.focus();
      });
    }

    finishContributionModal(restoreFocus = true) {
      const modal = this.contributionModal;
      if (!modal) return;
      this.contributionModalObserver?.disconnect();
      modal.inert = this.contributionModalWasInert;
      this.querySelector('[data-cart-panel]')?.removeAttribute('inert');
      const opener = this.contributionModalOpener;
      this.contributionModal = null;
      this.contributionModalOpener = null;
      if (restoreFocus && this.dataset.state === 'open' && opener?.isConnected) opener.focus();
    }

    restoreInteractionFocus() {
      const intent = this.focusAfterRefresh;
      this.focusAfterRefresh = null;
      if (!intent || (this.dataset.state !== 'open' && !this.pageMode)) return;

      if (intent.type === 'recommendation') {
        const controls = this.focusableElements().filter((element) => element.matches(
          '[data-cart-recommendation-add], .nm-cart__recommendation-select'
        ));
        const recommendation = controls[Math.min(intent.index, Math.max(0, controls.length - 1))];
        const fallback = recommendation
          || this.querySelector('.nm-cart__recommendations > summary')
          || this.querySelector('[data-cart-checkout]')
          || this.querySelector('[data-cart-panel]');
        fallback?.focus();
        return;
      }

      if (intent.type === 'undo') {
        const restoredRow = Array.from(this.querySelectorAll('[data-cart-line]')).find((row) => (
          row.dataset.variantId === intent.variantId
        ));
        const fallback = restoredRow?.querySelector('[data-cart-qty-increase], [data-cart-remove]')
          || this.querySelector('[data-cart-undo-action]:not(:disabled)')
          || this.querySelector('[data-cart-checkout]')
          || this.querySelector('[data-cart-panel]');
        fallback?.focus();
        return;
      }

      const rows = Array.from(this.querySelectorAll('[data-cart-line]'));
      const sameRow = rows.find((row) => row.dataset.lineKey === intent.key);
      const selector = intent.action === 'increase'
        ? '[data-cart-qty-increase]'
        : intent.action === 'decrease'
          ? '[data-cart-qty-decrease]'
          : '[data-cart-remove]';

      if (sameRow?.querySelector(selector)) {
        sameRow.querySelector(selector).focus();
        return;
      }

      const adjacentRow = rows[Math.min(intent.index, Math.max(0, rows.length - 1))];
      const fallback = adjacentRow?.querySelector('[data-cart-remove], [data-cart-qty-increase]')
        || this.querySelector('[data-cart-checkout]')
        || this.querySelector('[data-cart-panel]');
      fallback?.focus();
    }

    setOpen(open) {
      if (this.pageMode) return;

      if (open) {
        window.clearTimeout(this.closeTimer);
        if (!this.pageLocked) {
          if (!this.opener || !document.contains(this.opener)) this.opener = document.activeElement;
          this.lockPage();
        }

        const wasHidden = this.hidden;
        this.hidden = false;
        if (wasHidden) {
          this.dataset.state = 'closed';
          this.querySelector('[data-cart-panel]')?.getBoundingClientRect();
        }
        requestAnimationFrame(() => {
          if (this.hidden) return;
          this.dataset.state = 'open';
          requestAnimationFrame(() => this.querySelector('[data-cart-panel]')?.focus());
        });
        return;
      }

      if (this.dataset.state !== 'open') return;
      this.contributionModal?.shadowRoot?.querySelector('.modal-close')?.click();
      this.finishContributionModal(false);
      this.dataset.state = 'closed';
      const opener = this.opener;

      window.clearTimeout(this.closeTimer);
      const closeDelay = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? 0
        : CLOSE_ANIMATION_MS;
      this.closeTimer = window.setTimeout(() => {
        if (this.dataset.state !== 'closed') return;
        this.hidden = true;
        this.unlockPage();
        if (opener?.focus && document.contains(opener)) opener.focus();
      }, closeDelay);
    }

    lockPage() {
      if (this.pageLocked) return;
      this.pageLocked = true;
      const count = (window.__emeScrollLocks = (window.__emeScrollLocks || 0) + 1);
      document.documentElement.classList.add('nm-cart-open');
      this.setBackgroundInert(true);
      if (count === 1) {
        window.__nmCartScrollStyle = {
          overflow: document.documentElement.style.overflow,
          height: document.documentElement.style.height,
        };
        document.documentElement.style.overflow = 'hidden';
        document.documentElement.style.height = '100%';
      }
    }

    unlockPage() {
      if (!this.pageLocked) return;
      this.pageLocked = false;
      this.setBackgroundInert(false);
      if (!window.__emeScrollLocks) return;
      window.__emeScrollLocks -= 1;
      if (window.__emeScrollLocks !== 0) return;
      document.documentElement.classList.remove('nm-cart-open');
      const previous = window.__nmCartScrollStyle || {};
      document.documentElement.style.overflow = previous.overflow || '';
      document.documentElement.style.height = previous.height || '';
      delete window.__nmCartScrollStyle;
    }

    setBackgroundInert(inert) {
      if (inert) {
        let componentRoot = this;
        while (componentRoot.parentElement && componentRoot.parentElement !== document.body) {
          componentRoot = componentRoot.parentElement;
        }
        this.inertedElements = Array.from(document.body.children)
          .filter((element) => element !== componentRoot)
          .map((element) => ({ element, wasInert: element.inert }));
        this.inertedElements.forEach(({ element }) => { element.inert = true; });
        return;
      }

      this.inertedElements.forEach(({ element, wasInert }) => {
        if (document.contains(element)) element.inert = wasInert;
      });
      this.inertedElements = [];
    }

    route(path) {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      return `${root.replace(/\/$/, '')}/${path.replace(/^\//, '')}`;
    }

    add(form, explicitSubmitter) {
      const submitter = explicitSubmitter || form.querySelector('[type="submit"]');
      if (form.matches('[data-cart-recommendation-form]')) {
        const recommendationForms = Array.from(this.querySelectorAll('[data-cart-recommendation-form]'));
        this.focusAfterRefresh = {
          type: 'recommendation',
          index: Math.max(0, recommendationForms.indexOf(form)),
        };
      }
      if (!this.pageMode && submitter instanceof HTMLElement && document.contains(submitter)) {
        this.opener = submitter;
      }
      const body = new FormData(form);
      if (submitter?.name) body.set(submitter.name, submitter.value);
      body.set('sections', this.sectionId);
      body.set('sections_url', window.location.pathname);

      const externalSubmitter = submitter instanceof HTMLElement && !this.contains(submitter);
      const submitterWasDisabled = Boolean(externalSubmitter && submitter.disabled);
      if (externalSubmitter) {
        submitter.disabled = true;
        submitter.setAttribute('aria-busy', 'true');
      }

      if (!this.pageMode) this.setOpen(true);
      this.startBusy();

      return this.enqueueMutation(async () => {
        try {
          const response = await this.fetchWithTimeout(this.route('cart/add.js'), {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body,
          });
          const payload = await response.json().catch(() => ({}));
          if (!response.ok) throw this.responseError(response, payload, nmT('cart_add_error', 'Vi kunne ikke lægge varen i kurven. Prøv igen.'));

          const applied = await this.afterPendingRefreshes(() => (
            this.applySectionHtml(payload?.sections?.[this.sectionId])
          ));
          if (!applied) await this.refresh();
          this.scrollToLatestItem();
        } catch (error) {
          this.showError(error.userMessage);
        } finally {
          this.endBusy();
          if (externalSubmitter) {
            if (!submitterWasDisabled) submitter.disabled = false;
            submitter.removeAttribute('aria-busy');
          }
        }
      });
    }

    enqueueMutation(task) {
      const operation = (this.mutationQueue || Promise.resolve())
        .catch(() => undefined)
        .then(task);
      this.mutationQueue = operation;
      return operation;
    }

    async afterPendingRefreshes(apply) {
      // A refresh can be appended while an earlier one is settling. Observe the queue until its
      // tail stays stable, then apply synchronously in this same microtask. Keeping the barrier
      // and DOM write together closes the gap where a newly queued stale refresh could otherwise
      // slip between an awaited barrier and the mutation response.
      while (true) {
        const observedQueue = this.refreshQueue;
        if (observedQueue) await observedQueue.catch(() => undefined);
        if (observedQueue === this.refreshQueue) return apply();
      }
    }

    queueQuantityChange(key, quantity, row) {
      const nextQuantity = Math.max(0, quantity);
      const renderedQuantity = Number.parseInt(
        row?.dataset.lineQuantity || row?.querySelector('[data-cart-qty-value]')?.textContent || '0',
        10
      ) || 0;
      if (this.confirmedQuantities[key] === undefined) {
        this.confirmedQuantities[key] = renderedQuantity;
      }
      this.pendingQuantities[key] = nextQuantity;
      const printed = row?.querySelector('[data-cart-qty-value]');
      if (printed) printed.textContent = String(nextQuantity);
      row?.setAttribute('aria-busy', 'true');
      this.startBusy();

      return this.enqueueMutation(async () => {
        try {
          const queuedQuantity = this.pendingQuantities[key];
          if (queuedQuantity === undefined) return true;

          this.setLineBusy(key, true);
          const succeeded = await this.performQuantityChange(key, queuedQuantity);
          if (this.pendingQuantities[key] === queuedQuantity) {
            delete this.pendingQuantities[key];
            if (!succeeded) {
              const currentRow = Array.from(this.querySelectorAll('[data-cart-line]')).find((candidate) => (
                candidate.dataset.lineKey === key
              ));
              const value = currentRow?.querySelector('[data-cart-qty-value]');
              if (value) value.textContent = String(this.confirmedQuantities[key]);
            }
            delete this.confirmedQuantities[key];
          }
          return succeeded;
        } finally {
          if (this.pendingQuantities[key] === undefined) this.setLineBusy(key, false);
          this.endBusy();
        }
      });
    }

    showUndo(item) {
      if (!item) return;
      this.pendingUndo = item;
      this.undoExpiresAt = Date.now() + UNDO_TIMEOUT_MS;
      this.renderUndo();
    }

    renderUndo() {
      const item = this.pendingUndo;
      const undo = this.querySelector('[data-cart-undo]');
      if (!item || !undo) return;

      const remaining = Math.max(0, (this.undoExpiresAt || 0) - Date.now());
      if (remaining <= 0) {
        this.hideUndo();
        return;
      }

      const summary = this.querySelector('.nm-cart__summary');
      if (summary) {
        this.style.setProperty('--nm-cart-summary-height', `${Math.ceil(summary.getBoundingClientRect().height)}px`);
      }
      const message = undo.querySelector('[data-cart-undo-message]');
      if (message) message.textContent = nmT('cart_item_removed', '{title} er fjernet.', { title: item.title });
      undo.hidden = false;
      window.clearTimeout(this.undoTimer);
      this.undoTimer = window.setTimeout(() => this.hideUndo(), remaining);
    }

    hideUndo() {
      const undo = this.querySelector('[data-cart-undo]');
      const activeElement = this.deepActiveElement();
      const focusWasInside = Boolean(undo && activeElement && undo.contains(activeElement));
      if (undo) undo.hidden = true;
      this.pendingUndo = null;
      this.undoExpiresAt = null;
      window.clearTimeout(this.undoTimer);

      if (focusWasInside && !this.focusAfterRefresh) {
        const fallback = this.querySelector('[data-cart-checkout]')
          || this.querySelector('[data-cart-panel]');
        window.requestAnimationFrame(() => fallback?.focus());
      }
    }

    undoRemove() {
      const item = this.pendingUndo;
      if (!item) return Promise.resolve(false);
      this.focusAfterRefresh = { type: 'undo', variantId: String(item.id) };
      const undo = this.querySelector('[data-cart-undo]');
      if (undo) undo.hidden = true;
      window.clearTimeout(this.undoTimer);
      this.startBusy();
      let restoredOnServer = false;

      return this.enqueueMutation(async () => {
        try {
          const restoredItem = {
            id: item.id,
            quantity: item.quantity,
          };
          if (item.properties && Object.keys(item.properties).length > 0) {
            restoredItem.properties = item.properties;
          }
          if (item.sellingPlan) restoredItem.selling_plan = item.sellingPlan;

          const response = await this.fetchWithTimeout(this.route('cart/add.js'), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({
              items: [restoredItem],
              sections: [this.sectionId],
              sections_url: window.location.pathname,
            }),
          });
          const payload = await response.json().catch(() => ({}));
          if (!response.ok) throw this.responseError(response, payload, nmT('cart_restore_error', 'Vi kunne ikke gendanne varen. Prøv igen.'));
          restoredOnServer = true;
          this.hideUndo();

          const applied = await this.afterPendingRefreshes(() => (
            this.applySectionHtml(payload?.sections?.[this.sectionId])
          ));
          if (!applied) await this.refresh();
          this.scrollToLatestItem();
          return true;
        } catch (error) {
          this.showError(error.userMessage || nmT('cart_restore_error', 'Vi kunne ikke gendanne varen. Prøv igen.'));
          if (!restoredOnServer) this.showUndo(item);
          return false;
        } finally {
          this.endBusy();
        }
      });
    }

    async performQuantityChange(key, quantity) {
      let expectedCount = null;
      let mutationSucceeded = false;
      try {
        const response = await this.fetchWithTimeout(this.route('cart/change.js'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            id: key,
            quantity,
            sections: [this.sectionId],
            sections_url: window.location.pathname,
          }),
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw this.responseError(response, payload, nmT('cart_quantity_error', 'Vi kunne ikke opdatere antallet. Prøv igen.'));
        mutationSucceeded = true;
        const payloadCount = Number(payload?.item_count);
        expectedCount = Number.isFinite(payloadCount) ? payloadCount : null;
        const applied = await this.afterPendingRefreshes(() => (
          this.applySectionHtml(payload?.sections?.[this.sectionId], expectedCount)
        ));
        if (!applied) {
          await this.refresh(expectedCount);
        }
        return true;
      } catch (error) {
        // A successful cart mutation and its section render are two different network results.
        // If Shopify's section proxy briefly returns pre-mutation HTML, keep asking for the
        // exact API-confirmed count instead of accepting a valid-but-stale drawer.
        const recovered = await this.refresh(expectedCount).then(() => true).catch(() => false);
        if (!mutationSucceeded || !recovered) this.showError(error.userMessage);
        return mutationSucceeded;
      }
    }

    refresh(expectedCount = null) {
      // Keep Section Rendering calls ordered. An open-refresh may already be in flight when an
      // add completes; queuing guarantees the post-mutation render is the last one applied.
      this.refreshQueue = (this.refreshQueue || Promise.resolve())
        .catch(() => undefined)
        .then(() => this.performRefresh(expectedCount));
      return this.refreshQueue;
    }

    async performRefresh(expectedCount = null) {
      this.startBusy();
      try {
        let lastError;
        for (let attempt = 1; attempt <= REFRESH_ATTEMPTS; attempt += 1) {
          try {
            const base = window.location.pathname;
            const url = new URL(base, window.location.origin);
            url.searchParams.set('sections', this.sectionId);
            url.searchParams.set('_nm_cart', String(Date.now()));

            const response = await this.fetchWithTimeout(url.toString(), {
              headers: { Accept: 'application/json' },
            });
            if (!response.ok) throw new Error(`Cart refresh failed (${response.status})`);

            const sections = await response.json();
            const html = sections && sections[this.sectionId];
            if (typeof html !== 'string' || !html.trim()) throw new Error('Cart section was empty');

            if (!this.applySectionHtml(html, expectedCount)) {
              throw new Error('Cart section content was missing or stale');
            }
            return;
          } catch (error) {
            lastError = error;
            if (attempt < REFRESH_ATTEMPTS) await wait(REFRESH_RETRY_MS * attempt);
          }
        }
        // Shopify's development/preview proxy occasionally returns a valid section-response
        // object whose value is null (most often in WebKit). A normal storefront document still
        // contains the server-rendered overlay group, so use that as the final deterministic
        // fallback instead of leaving quantity, subtotal and header-count out of sync.
        if (await this.refreshFromDocument(expectedCount)) return;
        throw lastError || new Error('Cart refresh failed');
      } finally {
        this.endBusy();
      }
    }

    async refreshFromDocument(expectedCount = null) {
      try {
        const url = new URL(window.location.pathname, window.location.origin);
        url.searchParams.set('_nm_cart_document', String(Date.now()));
        const response = await this.fetchWithTimeout(url.toString(), {
          headers: { Accept: 'text/html' },
          cache: 'no-store',
        });
        if (!response.ok) return false;

        const html = await response.text();
        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const freshDrawer = Array.from(documentFragment.querySelectorAll('[data-cart-drawer]')).find((drawer) => (
          drawer.dataset.sectionId === this.sectionId
          && drawer.dataset.pageMode === this.dataset.pageMode
        ));
        return this.applyContentElement(freshDrawer?.querySelector('[data-cart-content]'), expectedCount);
      } catch (error) {
        return false;
      }
    }

    applySectionHtml(html, expectedCount = null) {
      if (typeof html !== 'string' || !html.trim()) return false;
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      return this.applyContentElement(documentFragment.querySelector('[data-cart-content]'), expectedCount);
    }

    applyContentElement(freshContent, expectedCount = null) {
      const currentContent = this.querySelector('[data-cart-content]');
      if (!freshContent || !currentContent) return false;

      const freshCount = Number(freshContent.dataset.count);
      if (expectedCount !== null && (!Number.isFinite(freshCount) || freshCount !== expectedCount)) {
        return false;
      }

      currentContent.className = freshContent.className;
      currentContent.innerHTML = freshContent.innerHTML;
      currentContent.dataset.count = freshContent.dataset.count || '0';
      currentContent.setAttribute('aria-busy', this.busyCount > 0 ? 'true' : 'false');

      if (this.busyCount > 0) this.setControlsBusy(true);
      if (this.pendingUndo) this.renderUndo();
      this.synchronizeGlobalCount(Number(currentContent.dataset.count) || 0);
      if (this.busyCount === 0) this.restoreInteractionFocus();
      return true;
    }

    synchronizeGlobalCount(count) {
      document.querySelectorAll('[data-cart-count]').forEach((element) => {
        element.textContent = String(count);
      });

      const hasItems = count > 0;
      document.querySelectorAll('[data-cart-bag-empty]').forEach((element) => {
        element.hidden = hasItems;
      });
      document.querySelectorAll('[data-cart-bag-full]').forEach((element) => {
        element.hidden = !hasItems;
      });

      const detail = { count };
      document.dispatchEvent(new CustomEvent('nm:cart:updated', { detail }));
      document.dispatchEvent(new CustomEvent('eme:cart:updated', { detail }));
    }

    startBusy() {
      this.busyCount += 1;
      this.dataset.loading = 'true';
      this.querySelector('[data-cart-content]')?.setAttribute('aria-busy', 'true');
      this.setControlsBusy(true);
    }

    endBusy() {
      this.busyCount = Math.max(0, this.busyCount - 1);
      if (this.busyCount !== 0) return;
      this.dataset.loading = 'false';
      this.querySelector('[data-cart-content]')?.setAttribute('aria-busy', 'false');
      this.setControlsBusy(false);
      this.querySelectorAll('[data-cart-line][aria-busy="true"]').forEach((row) => {
        row.removeAttribute('aria-busy');
      });
      this.restoreInteractionFocus();
    }

    setControlsBusy(busy) {
      const selector = [
        '[data-cart-checkout]',
        '[data-cart-remove]',
        '[data-cart-recommendation-add]',
        '[data-cart-undo-action]',
      ].join(', ');

      this.querySelectorAll(selector).forEach((control) => {
        if (busy) {
          if (control.disabled || control.dataset.cartBusyDisabled === 'true') return;
          control.dataset.cartBusyDisabled = 'true';
          control.disabled = true;
          control.setAttribute('aria-disabled', 'true');
          return;
        }

        if (control.dataset.cartBusyDisabled !== 'true') return;
        control.disabled = false;
        control.removeAttribute('aria-disabled');
        delete control.dataset.cartBusyDisabled;
      });
    }

    setLineBusy(key, busy) {
      const row = Array.from(this.querySelectorAll('[data-cart-line]')).find((candidate) => (
        candidate.dataset.lineKey === key
      ));
      if (!row) return;
      if (busy) row.setAttribute('aria-busy', 'true');
      else row.removeAttribute('aria-busy');
    }

    async fetchWithTimeout(input, options = {}) {
      const controller = new AbortController();
      this.activeRequests.add(controller);
      const timeout = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

      try {
        return await fetch(input, { ...options, signal: controller.signal });
      } catch (error) {
        if (error?.name !== 'AbortError') throw error;
        const timeoutError = new Error('Cart request timed out');
        timeoutError.userMessage = nmT('cart_timeout', 'Forbindelsen tog for lang tid. Kontrollér nettet og prøv igen.');
        throw timeoutError;
      } finally {
        window.clearTimeout(timeout);
        this.activeRequests.delete(controller);
      }
    }

    scrollToLatestItem() {
      const scroller = this.querySelector('[data-cart-scroll-root]');
      const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      if (scroller) scroller.scrollTo({ top: 0, behavior });
    }

    responseError(response, payload, fallback) {
      let message = fallback;
      const source = typeof payload?.description === 'string'
        ? payload.description
        : typeof payload?.message === 'string' ? payload.message : '';

      if (response.status === 422) {
        const stockMatch = source.match(/(?:only|maximum of)\s+(\d+)/i);
        message = stockMatch
          ? nmT('cart_stock_limit', 'Der er kun {count} tilbage på lager.', { count: stockMatch[1] })
          : nmT('cart_stock_unavailable', 'Det ønskede antal er desværre ikke på lager. Justér antallet og prøv igen.');
      }

      const error = new Error(source || message);
      error.userMessage = message;
      return error;
    }

    showError(message) {
      const error = this.querySelector('[data-cart-error]');
      if (!error) return;
      const text = error.querySelector('[data-cart-error-message]');
      if (text) text.textContent = message || error.dataset.cartDefaultError || nmT('cart_generic_error', 'Der opstod en fejl. Prøv igen.');
      error.hidden = false;
      window.clearTimeout(this.errorTimer);
    }

    hideError() {
      const error = this.querySelector('[data-cart-error]');
      if (error) error.hidden = true;
      window.clearTimeout(this.errorTimer);
    }
  }

  customElements.define('nm-cart-drawer', NeverMondayCartDrawer);
})();
